import { useRef, useState } from "react";
import { Field, Form, Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { useLocation, useParams } from "react-router-dom";
import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MilestonesWrapper from "../../shared/components/admin/MilestonesWrapper";
import SprintWrapper from "../../shared/components/admin/SprintWrapper";
import { FormikQuillField } from "../../shared/components/formfields/FormikQuillField";
import { TextFormField } from "../../shared/components/formfields/TextFormField";
import { AutoCompleteField } from "../../shared/components/formfields/AutoCompleteField";
import CustomDivider from "../../shared/utils/CustomDivider";
import { useGetLmsSubtopicDetailForEditingThreejs } from "../../assets/api/useAdminApi";
import { useLmsDropdownOptions } from "./option/LsmOption";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToaster } from "../../shared/utils/ToasterProvider";
import { postAddSubtopicThreejs } from "../../assets/nodeApi/postApi/postAdminApi";
import { putUpdateThreejsSubtopic } from "../../assets/nodeApi/putApi/putAdminApi";
import { RadioButtonGroupFormField } from "../../shared/components/formfields/RadioButtonField";
import ToggleSwitch from "../../shared/utils/CustomToggle";
import { Grid_Gap, Input_Width_LG } from "../../shared/constants";

const AddEditSubtopicThree = () => {
  const { subtopicId, topicId } = useParams();
  const queryClient = useQueryClient();
  const { showToaster } = useToaster();
  const formikRef = useRef();
  const location = useLocation();
  const { topic} = location.state || {};
  const { lmsParagraLanguage, lmsQuestionSourceOptionThreejs, lmsQuestionTypeOptionThreejs } = useLmsDropdownOptions();
  const { data: subtopicDetail, isLoading } = useGetLmsSubtopicDetailForEditingThreejs(subtopicId);

  const mutationUpdate = useMutation({
    mutationFn: ({ formData }) => putUpdateThreejsSubtopic(formData, subtopicId),
  });

  const mutationAdd = useMutation({
    mutationFn: ({ formData }) => postAddSubtopicThreejs(topicId, formData),
  });
  const isUpdating = mutationUpdate.isPending;
  const isAdding = mutationAdd.isPending;
  if (isLoading) return <div>Loading...</div>;

  const initialValues = {
    name: subtopicDetail?.subtopic?.name || "",
    description: subtopicDetail?.subtopic?.description || "",
    paragraphs:
      subtopicDetail?.subtopic?.paragraphs?.map((p) => ({
        id: p.id,
        description: p.description || "",
        language: p.language || "",
        questions:
          p.questions?.map((q) => ({
            id: q.id,
            question_text: q.question_text || "",
            question_type: q.question_type || "",
            question_source: q.question_source || "",
            difficulty_level: q.difficulty_level || "",
            understanding_level: q.understanding_level || "",
            answers:
              q.answers?.map((a) => ({
                id: a.id,
                answer_text: a.answer_text || "",
                is_correct: a.is_correct || false,
                display_order: a.display_order || "",
                max_understanding_level: a.max_understanding_level || "",
                min_understanding_level: a.min_understanding_level || "",
              })) || [],
          })) || [],


      })) || [
        {
          description: "",
          language: "",
          questions: [
            {
              question_text: "",
              question_type: "",
              question_source: "",
              difficulty_level: "",
              understanding_level: "",
              answers: [
                {
                  answer_text: "",
                  is_correct: true,
                  display_order: "",
                  max_understanding_level: "",
                  min_understanding_level: "",
                },
              ],
            },
          ],
        },
      ],



  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Subtopic name required"),
    description: Yup.string().required("Description required"),
  });

  const handleSubmit = (values) => {

    const updatedParagraphs = values.paragraphs.map((p) => ({
      ...p,
      questions: p.questions.map((q) => ({
        ...q,
        answers: q.answers.map((a, index) => ({
          ...a,
          display_order: index + 1
        }))
      }))
    }));

    const payload = {
      name: values.name,
      description: values.description,
      paragraphs: updatedParagraphs
    };

    if (subtopicId) {
      mutationUpdate.mutate(
        { formData: payload },
        {
          onSuccess: () => {
            showToaster("Subtopic Updated", "", "success");
            queryClient.invalidateQueries(["lms/subtopicforediting"]);
          }
        });
    } else {
      mutationAdd.mutate(
        { formData: payload },
        {
          onSuccess: () => {
            showToaster("Subtopic Added", "", "success");
            queryClient.invalidateQueries(["lms/subtopicforediting"]);
          }
        });
    }
  };

  const DificultyLevelOptions = [
    { id: 1, label: "Easy", value: "Easy" },
    { id: 2, label: "Medium", value: "Medium" },
    { id: 3, label: "Hard", value: "Hard" },

  ];




  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <Form>
          <MilestonesWrapper
            headerText={`${subtopicId ? "Update" : "Add"} Script`}
            sx={{ border: "1px solid #4169E1" }}
          >
            <Typography variant="body1" fontWeight={600}>{topic?.name || ""}</Typography>
            <CustomDivider />
            <Grid2 container spacing={Grid_Gap}>
              <Grid2 size={12}>
                <Field
                  name="name"
                  component={TextFormField}
                  label="Subtopic Name"
                  isRequired
                />
              </Grid2>

              <Grid2 size={12}>
                <Field
                  name="description"
                  component={TextFormField}
                  label="Description"
                  multiline
                  rows={3}
                  isRequired
                />
              </Grid2>
            </Grid2>

            <CustomDivider />

            {/* PARAGRAPHS */}
            <FieldArray name="paragraphs">
              {({ push, remove }) => (
                <>
                  {values.paragraphs.map((paragraph, pIndex) => (
                    <SprintWrapper
                      key={pIndex}
                      headerText={`Paragraph ${pIndex + 1}`}
                      onDelete={() => remove(pIndex)}
                    >
                      <Grid2 container spacing={Grid_Gap}>
                        <Grid2 size={12}>
                          <Field
                            name={`paragraphs.${pIndex}.description`}
                            component={FormikQuillField}
                            label="Paragraph"
                          />
                        </Grid2>

                        <Grid2 size={12}>
                          <Field
                            name={`paragraphs.${pIndex}.language`}
                            component={AutoCompleteField}
                            options={lmsParagraLanguage.options}
                            label="Language"
                          />
                        </Grid2>
                      </Grid2>

                      {/* QUESTIONS */}
                      <FieldArray name={`paragraphs.${pIndex}.questions`}>

                        <>
                          {paragraph.questions?.map((question, qIndex) => (
                            <Box
                              key={qIndex}
                              sx={{ border: "1px solid #ddd", p: 2, mt: 2 }}
                            >
                              <Grid2 container spacing={Grid_Gap}>

                                {/* Question Text */}
                                <Grid2 size={Input_Width_LG}>
                                  <Field
                                    name={`paragraphs.${pIndex}.questions.${qIndex}.question_text`}
                                    component={TextFormField}
                                    label="Question Text"
                                  />
                                </Grid2>



                                {/* Question Source */}
                                <Grid2 size={Input_Width_LG}>
                                  <Field
                                    name={`paragraphs.${pIndex}.questions.${qIndex}.question_source`}
                                    component={AutoCompleteField}
                                    options={lmsQuestionSourceOptionThreejs.options}
                                    label="Question Source"
                                  />
                                </Grid2>

                                {/* Question Type */}
                                <Grid2 size={Input_Width_LG}>
                                  <Field
                                    name={`paragraphs.${pIndex}.questions.${qIndex}.question_type`}
                                    component={AutoCompleteField}
                                    options={lmsQuestionTypeOptionThreejs.options}
                                    label="Question Type"
                                  />
                                </Grid2>




                                {/* Understanding Level */}
                                <Grid2 size={Input_Width_LG}>
                                  <Field
                                    name={`paragraphs.${pIndex}.questions.${qIndex}.understanding_level`}
                                    component={TextFormField}
                                    label="Understanding Level"
                                    type="number"
                                  />
                                </Grid2>
                                <Grid2 size={Input_Width_LG}>
                                  <Field
                                    name={`paragraphs.${pIndex}.questions.${qIndex}.difficulty_level`}
                                    component={RadioButtonGroupFormField}
                                    options={DificultyLevelOptions}
                                    row
                                  />
                                </Grid2>

                              </Grid2>

                              {/* ANSWERS */}
                              <FieldArray
                                name={`paragraphs.${pIndex}.questions.${qIndex}.answers`}
                              >
                                {({ push: pushAnswer, remove: removeAnswer }) => (
                                  <>
                                    {question.answers?.map((answer, aIndex) => (
                                      <Box
                                        key={aIndex}
                                        sx={{
                                          border: "1px dashed gray",
                                          p: 2,
                                          mt: 2
                                        }}
                                      >
                                        <Grid2 container spacing={Grid_Gap}>
                                          <Grid2 size={12}>
                                            <Field
                                              name={`paragraphs.${pIndex}.questions.${qIndex}.answers.${aIndex}.is_correct`}
                                            >

                                              {({ field, form }) => (
                                                <Stack direction="row" justifyContent="flex-end">
                                                  <ToggleSwitch
                                                    checked={Boolean(field.value)}
                                                    onCheck={(val) =>
                                                      form.setFieldValue(field.name, val)
                                                    }
                                                  />
                                                </Stack>
                                              )}

                                            </Field>
                                          </Grid2>
                                          <Grid2 size={Input_Width_LG}>
                                            <Field
                                              name={`paragraphs.${pIndex}.questions.${qIndex}.answers.${aIndex}.answer_text`}
                                              component={TextFormField}
                                              label="Answer"
                                            />
                                          </Grid2>

                                          <Grid2 size={Input_Width_LG}>
                                            <Field
                                              name={`paragraphs.${pIndex}.questions.${qIndex}.answers.${aIndex}.max_understanding_level`}
                                              component={TextFormField}
                                              label="Max Understanding Level"
                                              type="number"
                                            />
                                          </Grid2>
                                          <Grid2 size={Input_Width_LG}>
                                            <Field
                                              name={`paragraphs.${pIndex}.questions.${qIndex}.answers.${aIndex}.min_understanding_level`}
                                              component={TextFormField}
                                              label="Min Understanding Level"
                                              type="number"
                                            />
                                          </Grid2>


                                        </Grid2>

                                      </Box>
                                    ))}
                                  </>
                                )}
                              </FieldArray>

                            </Box>
                          ))}
                        </>

                      </FieldArray>
                    </SprintWrapper>
                  ))}

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      onClick={() =>
                        push({
                          description: "",
                          language: "",
                          questions: [
                            {
                              question_text: "",
                              question_type: "",
                              question_source: "",
                              difficulty_level: "",
                              understanding_level: "",
                              answers: [
                                {
                                  answer_text: "",
                                  is_correct: true,
                                  display_order: "",
                                  max_understanding_level: "",
                                  min_understanding_level: ""
                                }
                              ]
                            }
                          ]
                        })
                      }
                    >
                      Add Paragraph
                    </Button>
                  </Box>
                </>
              )}
            </FieldArray>
          </MilestonesWrapper>

          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdating || isAdding}
            >
              {subtopicId
                ? isUpdating
                  ? "Updating..."
                  : "Update Script"
                : isAdding
                  ? "Adding..."
                  : "Add Script"}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default AddEditSubtopicThree;